import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field, Btn, StatusBadge } from "@/components/shared";

export function Profile() {
  const { user, reloadUser } = useAuth();
  const [f, setF] = useState({ email: user.email || "", shop_name: user.shop_name || "", address: user.address || "", city: user.city || "", state: user.state || "", pincode: user.pincode || "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.put("/profile", f); await reloadUser(); toast.success("Profile updated"); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  const ro = [["Login ID", user.login_id], ["Post ID", user.post_id], ["Name", user.name], ["Mobile No", user.mobile], ["Gender", user.gender || "-"],
    ["Aadhaar No", user.aadhaar ? "XXXX XXXX " + user.aadhaar.slice(-4) : "-"], ["PAN No", user.pan || "-"], ["Member since", fmtDate(user.created_at)]];
  return (
    <div>
      <PageHeader title="My Profile" />
      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        <Card title="Account" action={<StatusBadge status={user.kyc_status} />}>
          <div className="grid grid-cols-2 gap-4 text-sm" data-testid="profile-readonly">
            {ro.map(([k, v]) => <div key={k}><div className="np-label">{k}</div><div className="font-semibold">{v}</div></div>)}
          </div>
          <Link to="/kyc" className="mt-5 inline-block text-sm font-semibold text-blue-700" data-testid="goto-kyc-link">Update KYC →</Link>
        </Card>
        <Card title="Contact & shop">
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="profile-form">
            {[["email", "Email"], ["shop_name", "Shop / Firm Name"], ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, l]) => (
              <Field key={k} label={l}><input className="np-input" value={f[k]} onChange={set(k)} data-testid={`profile-${k}-input`} /></Field>
            ))}
            <Btn type="submit" loading={loading} data-testid="profile-save-button">Save</Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

export function Kyc() {
  const { user, reloadUser } = useAuth();
  const [f, setF] = useState({ aadhaar: user.aadhaar || "", pan: user.pan || "", bank_account: user.bank_account || "", ifsc: user.ifsc || "", address: user.address || "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post("/profile/kyc", f); toast.success(data.message); reloadUser(); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title="Update KYC" subtitle="Submit your KYC details. Admin will verify and approve."><StatusBadge status={user.kyc_status} /></PageHeader>
      <Card className="max-w-3xl">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="kyc-form">
          <Field label="Aadhaar Number"><input className="np-input font-mono" maxLength={12} value={f.aadhaar} onChange={set("aadhaar")} required data-testid="kyc-aadhaar-input" /></Field>
          <Field label="PAN Number"><input className="np-input font-mono uppercase" maxLength={10} value={f.pan} onChange={set("pan")} required data-testid="kyc-pan-input" /></Field>
          <Field label="Bank Account"><input className="np-input font-mono" value={f.bank_account} onChange={set("bank_account")} required data-testid="kyc-bank-input" /></Field>
          <Field label="IFSC"><input className="np-input font-mono uppercase" maxLength={11} value={f.ifsc} onChange={set("ifsc")} required data-testid="kyc-ifsc-input" /></Field>
          <Field label="Address" className="sm:col-span-2"><input className="np-input" value={f.address} onChange={set("address")} required data-testid="kyc-address-input" /></Field>
          <Btn type="submit" loading={loading} data-testid="kyc-submit-button">Submit KYC</Btn>
        </form>
      </Card>
    </div>
  );
}

function ChangeForm({ title, fields, url, keys, testid, numeric }) {
  const [f, setF] = useState({ a: "", b: "", c: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.b !== f.c) return toast.error(`New ${title} and confirm do not match`);
    setLoading(true);
    try { const { data } = await api.post(url, { [keys[0]]: f.a, [keys[1]]: f.b }); toast.success(data.message); setF({ a: "", b: "", c: "" }); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <Card title={`Change ${title}`}>
      <form onSubmit={submit} className="space-y-4" data-testid={`${testid}-form`}>
        {["a", "b", "c"].map((k, i) => (
          <Field key={k} label={fields[i]}>
            <input type="password" className="np-input font-mono" maxLength={numeric ? 4 : 50} inputMode={numeric ? "numeric" : undefined} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required data-testid={`${testid}-${k}-input`} />
          </Field>
        ))}
        <Btn type="submit" loading={loading} data-testid={`${testid}-submit-button`}>Update {title}</Btn>
      </form>
    </Card>
  );
}

export function Security() {
  return (
    <div>
      <PageHeader title="Password & MPIN" subtitle="MPIN is a 4-digit PIN used to authorise every transaction." />
      <div className="grid gap-6 lg:grid-cols-2">
        <ChangeForm title="Password" testid="change-password" url="/auth/change-password" keys={["old_password", "new_password"]} fields={["Old Password", "New Password", "Confirm Password"]} />
        <ChangeForm title="MPIN" testid="change-mpin" numeric url="/auth/change-mpin" keys={["old_mpin", "new_mpin"]} fields={["Old MPIN", "New MPIN", "Confirm MPIN"]} />
      </div>
    </div>
  );
}

export function IdCard() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader title="Agent ID Card"><Btn onClick={() => window.print()} data-testid="print-id-card-button"><Printer className="h-4 w-4" />Print</Btn></PageHeader>
      <div className="print-area w-[340px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="id-card">
        <div className="bg-gradient-to-r from-blue-700 to-indigo-900 px-5 py-4 text-white">
          <div className="font-display text-lg font-extrabold">ShubhPay</div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Authorised Retail Partner</div>
        </div>
        <div className="flex gap-4 p-5">
          <div className="grid h-20 w-20 shrink-0 place-items-center rounded-lg bg-slate-100 font-display text-2xl font-bold text-slate-500">{user.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}</div>
          <div className="text-sm">
            <div className="font-display text-base font-bold">{user.name}</div>
            <div className="text-slate-500">{user.shop_name}</div>
            <div className="mt-2 font-mono text-xs">ID: {user.login_id}</div>
            <div className="font-mono text-xs">Mob: {user.mobile}</div>
            <div className="text-xs">{[user.city, user.state].filter(Boolean).join(", ")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
          <StatusBadge status={user.kyc_status} />
          <QRCodeSVG value={`PHOTONPE|${user.login_id}|${user.mobile}`} size={56} />
        </div>
      </div>
    </div>
  );
}

const PAGES = {
  terms: { title: "Terms & Conditions", body: ["Retailers must complete KYC before using financial services.", "All transactions must be authorised using the retailer's MPIN. Never share your MPIN or password.", "Commission and charges are as per the slab list and may be revised by ShubhPay with notice.", "TDS is deducted on commission as per Income Tax rules.", "Misuse of AEPS/DMT services will lead to immediate account suspension."] },
  privacy: { title: "Privacy Policy", body: ["We collect customer Aadhaar, mobile and bank details only to process the requested transaction.", "Biometric data is never stored; it is encrypted and sent directly to the bank/NPCI.", "Retailer data is not shared with third parties except banking partners and regulators."] },
  refund: { title: "Refund Policy", body: ["Failed transactions are auto-refunded to the wallet within 24-72 hours.", "Pending DMT/Payout transactions are resolved by the bank within T+2 working days.", "Raise a complaint from the report page if a refund is not received."] },
  contact: { title: "Contact Us", body: ["ShubhPay Technologies", "Email: support@photonpe.com", "Helpline: 1800-000-0000 (10 AM - 7 PM, Mon-Sat)"] },
};

export function StaticPage() {
  const { slug } = useParams();
  const p = PAGES[slug] || PAGES.terms;
  return (
    <div>
      <PageHeader title={p.title} />
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(PAGES).map(([k, v]) => <Link key={k} to={`/page/${k}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${k === slug ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`} data-testid={`static-link-${k}`}>{v.title}</Link>)}
      </div>
      <Card className="max-w-3xl"><ul className="list-disc space-y-2 pl-5 text-sm text-slate-700" data-testid="static-content">{p.body.map((b) => <li key={b}>{b}</li>)}</ul></Card>
    </div>
  );
}
